import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  AreaNode,
  EDITOR_ACCENT,
  FloorArea,
  FloorTable,
  MIN_AREA_NODES,
  MIN_TABLE_SIZE,
  TableShape,
  TableStatus
} from '../shared/models/table-layout.model';
import {
  CLOSE_DRAFT_PX,
  centerOriginView,
  contentBounds,
  DISPLAY_SCALE_RANGE,
  EDITOR_SCALE_RANGE,
  fitAllView,
  formatMetres,
  isDoubleTap,
  MIN_BACKGROUND_WIDTH,
  polygonCentroid,
  polygonLabelAnchor,
  darken,
  ScaleRange,
  scaleBarFor,
  snapTo,
  toScreen,
  toWorld,
  ViewTransform,
  wheelFactor,
  withAlpha,
  ZOOM_IN_FACTOR,
  ZOOM_OUT_FACTOR,
  zoomAround,
  zoomCentered
} from '../shared/models/floor-plan-geometry';

/**
 * What a pointer gesture on empty canvas does.
 *
 * `'select'` pans, `'origin'` picks the new `(0, 0)`, `'draw'` drops polygon vertices — the three tools of the
 * handoff's toolbar. (`'draw'` is the old name for its *Bereich* tool and is kept so the host's existing wiring and
 * its tests keep reading naturally.)
 */
export type FloorPlanMode = 'select' | 'origin' | 'draw';

/** The background image as the canvas needs it: stored placement plus the resolved, locally derived render size. */
export interface RenderedBackground {
  /** Object URL of the fetched bytes (see `TableLayoutHttpService.downloadBackgroundImage`). */
  objectUrl: string;
  /** Left edge in floor-plan units. */
  x: number;
  /** Top edge in floor-plan units. */
  y: number;
  /** Rendered width in floor-plan units, already resolved through the "not placed yet" (`0`) case. */
  width: number;
  /** Rendered height in floor-plan units, always derived from {@link width} and the image's aspect ratio. */
  height: number;
  /** Rendering opacity, `0`–`1`. */
  opacity: number;
}

/**
 * How one table is painted in **display mode** — everything the read-only canvas needs to draw a marker, already
 * resolved by the host (#205).
 *
 * The canvas deliberately knows nothing about bookings, money or idle thresholds: it is a camera over a floor plan.
 * The Offene-Tische store resolves a reference's level and total into these plain strings and hands them over,
 * which keeps the escalation rules (and therefore the device configuration that drives them) in one place instead
 * of half in a canvas.
 */
export interface TableDecoration {
  /** The big label in the middle of the marker — the table number. */
  number: string;
  /** The small second line under it (the open total), or `null` for none (a free table). */
  detail: string | null;
  /** Marker fill. */
  surface: string;
  /** Marker outline. */
  border: string;
  /** Colour of both label lines. */
  textColor: string;
  /** Fill of the clock warning badge at the marker's top-right, or `null` when no badge is drawn. */
  badgeColor: string | null;
  /** Whether the marker is dimmed — how a free table is shown when the "freie Tische" toggle is on. */
  muted: boolean;
}

/** On-canvas size of a display-mode marker's number, in floor-plan units (the handover's 21 px in world space). */
const MARKER_NUMBER_CM = 21;

/** On-canvas size of a display-mode marker's detail line (the open total). */
const MARKER_DETAIL_CM = 11;

/** On-canvas diameter of the clock warning badge. */
const MARKER_BADGE_CM = 22;

/** How far the badge overhangs the marker's top-right corner, in floor-plan units. */
const MARKER_BADGE_OFFSET_CM = 7;

/**
 * How far a display-mode pointer may travel, in screen pixels, and still count as a **click** rather than a pan.
 *
 * The handover deselects on "a click, not a drag" on empty canvas: releasing after a real pan must leave the
 * selection alone, or every attempt to move the map throws away what the user was looking at.
 */
const CLICK_SLOP_PX = 3;

/** The gesture currently in flight (pointer down → up). */
type Gesture =
  // `moved` is only consulted in display mode, where a release that never travelled is the deselect click.
  | {kind: 'pan'; startX: number; startY: number; originX: number; originY: number; moved: boolean}
  | {kind: 'table'; id: string; startX: number; startY: number; originX: number; originY: number}
  | {kind: 'tableResize'; id: string; startX: number; startY: number; originWidth: number; originHeight: number}
  | {kind: 'area'; id: string; startX: number; startY: number; originNodes: readonly AreaNode[]}
  | {kind: 'vertex'; id: string; index: number; startX: number; startY: number; origin: AreaNode}
  | {kind: 'background'; startX: number; startY: number; originX: number; originY: number}
  | {kind: 'backgroundResize'; startX: number; startY: number; originWidth: number};

/** Constant on-canvas size of a table's number label, in floor-plan units (so it never grows with the table). */
const TABLE_LABEL_CM = 22;

/** Screen size of a drag handle (vertex dot, table resize grip) in pixels — the handover's 15 px. */
const HANDLE_PX = 15;

/** Screen size of the background image's resize grip in pixels — one pixel larger than {@link HANDLE_PX}. */
const BACKGROUND_HANDLE_PX = 16;

/** Screen thickness of an edge's add-node hit-zone in pixels. */
const EDGE_ZONE_PX = 14;

/** Screen diameter of a draft vertex dot in pixels. */
const DRAFT_DOT_PX = 13;

/**
 * Constant on-screen size of an area's name label, in pixels.
 *
 * The label lives inside the world layer (so it travels with the polygon) but must not grow with the zoom, so it is
 * rendered at `LABEL / scale` world units — exactly what the handover does.
 */
const AREA_LABEL_PX = 13;

/**
 * How far an area's own colour is darkened for its NAME LABEL in display mode.
 *
 * The editor achieves the same shade with `filter: brightness(0.7)`; display mode computes it as a value
 * instead, because a CSS filter is invisible to contrast tooling (see {@link darken}) and this screen's a11y
 * pass has to measure what a service actually reads across a room.
 */
const AREA_LABEL_SHADE = 0.7;

/** How long the button-driven camera moves (zoom ±, fit-all, centre-origin) are eased, in milliseconds. */
const VIEW_ANIMATION_MS = 340;

/** The easing of those moves — the handover's own curve. */
const VIEW_ANIMATION = '.34s cubic-bezier(.22,.61,.36,1)';

/**
 * The floor-plan canvas of the Tische-&-Bereiche admin screen (#201): an **infinite, pannable, zoomable** surface
 * with a metric grid, migrated from the Tischplan-Editor design handover.
 *
 * **The view transform is the whole model.** There is no fixed viewport and no scroll container: the world is drawn
 * into layers that carry one shared `translate(x, y) scale(s)` transform, so `screen = translate + world * scale`.
 * One world unit is **one centimetre** (which the stored `Table`/`Area` coordinates already are — a 100-unit default
 * table is 1 m), and the readout and scale bar are the only places that convert to metres. Panning changes the
 * translation, wheel-zoom changes the scale while holding the world point under the cursor still, and the origin
 * `(0, 0)` is a real, movable landmark rather than the top-left corner of a canvas.
 *
 * **Layering** (the handoff's §9, bottom to top): background image (world transform) → grid (screen space) →
 * world layer (areas → tables → origin marker → draft) → handle layer (screen space, constant-size handles) →
 * UI overlays (readout, banner, zoom cluster, scale bar, hint).
 *
 * **Every value in the template is the handover's.** Paddings, radii, shadows, font sizes and colours are copied
 * literally from `Tischplan Editor.dc.html` rather than mapped onto this app's utility scale, because the screen has
 * to be indistinguishable from the reference implementation when the two are put side by side. The accent is
 * therefore {@link EDITOR_ACCENT} (`#C50E6C`), not `theme-primary-600`.
 *
 * It stays **purely presentational + interactive**: it owns the *camera* (pan/zoom/cursor) but no data. Every
 * gesture is emitted and the host applies it to its draft, which is also what makes the live table→area match work —
 * the host re-resolves the moved table's area on every emitted move.
 *
 * **Accessibility.** The gesture surface is exposed as a single labelled image and its shapes are not focus targets:
 * pixel-dragging is inherently a pointer gesture, and duplicating it as dozens of tab stops would produce a keyboard
 * experience nobody can use. The *chrome* on top of that surface (zoom cluster) is real, labelled buttons and sits
 * **outside** the `role="img"` element so it is reachable, as are the host's toolbar and inspector — which is where
 * shape, size, seats, colour and opacity are reachable without a pointer.
 */
@Component({
  selector: 'app-floor-plan-canvas',
  standalone: true,
  imports: [],
  styleUrl: './floor-plan-editor.scss',
  styles: [`
    /* The canvas fills whatever region the host gives it; it is never a card in a scrolling page. */
    :host {
      display: block;
      position: absolute;
      inset: 0;
      overflow: hidden;
      /* The handover's canvas colour. It sits on the viewport (not on the grid layer) so a background image
         placed beneath the grid still shows through. */
      background: #e9e7e3;
    }

    .fpc-surface {
      position: absolute;
      inset: 0;
      touch-action: none;
      user-select: none;
    }

    .fpc-layer {
      position: absolute;
      left: 0;
      top: 0;
      transform-origin: 0 0;
      pointer-events: none;
    }

    /* 5 · UI overlays — the handover's floating panels, in its exact geometry. */
    .fpc-readout {
      position: absolute;
      top: 14px;
      left: 14px;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      padding: 8px 14px;
      border-radius: 12px;
      background: rgba(255, 255, 255, .9);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      box-shadow: 0 4px 14px -6px rgba(0, 0, 0, .2);
      font-size: 12.5px;
      font-weight: 500;
      color: #5c5f66;
      pointer-events: none;
      z-index: 10;
    }

    .fpc-readout span {
      font-variant-numeric: tabular-nums;
    }

    .fpc-readout b {
      color: #1c1c1e;
    }

    .fpc-readout .fpc-divider {
      width: 1px;
      height: 14px;
      background: #e2e0dc;
    }

    .fpc-banner {
      position: absolute;
      top: 14px;
      left: 50%;
      transform: translateX(-50%);
      display: inline-flex;
      align-items: center;
      gap: 9px;
      padding: 9px 18px;
      border-radius: 999px;
      background: #1c1c1e;
      color: #fff;
      font-size: 13.5px;
      font-weight: 500;
      box-shadow: 0 8px 22px -8px rgba(0, 0, 0, .4);
      pointer-events: none;
      z-index: 12;
    }

    .fpc-banner-dot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: #c50e6c;
    }

    /*
     * TOP-right, not the handovers' bottom-right — the one deliberate positional deviation on this screen, and
     * it applies to BOTH modes (editor and display).
     *
     * The app USED TO pin a global account switcher to the VIEWPORT at position: fixed; bottom: 1rem;
     * right: 1rem; z-index: 900 (x-components/account-switcher), rendered for social/PlatformOwner sessions.
     * Both handovers are standalone pages with no such furniture, so a bottom-right cluster sat underneath it:
     * in the editor it made the ⌖ centre-origin button unclickable, on Offene Tische the ⤢ fit-all button.
     *
     * That switcher has since been REMOVED, so the collision is gone — and the anchor deliberately does not move
     * back. The surviving context switcher takes the mirrored bottom-LEFT corner at the same z-index; the two
     * clusters (editor + Offene Tische) must stay anchored identically, so a user who knows where the camera
     * controls live in the Tischplan editor finds them in the same corner on the live view; and relocating
     * furniture that works, to reclaim a corner nothing needs, is a change with only downside. Everything about
     * the cluster itself (order, icons, sizes, radii, shadows, the accent button) is unchanged.
     */
    .fpc-zoom {
      position: absolute;
      right: 16px;
      top: 14px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      z-index: 10;
    }

    .fpc-zoom-stack {
      display: flex;
      flex-direction: column;
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 8px 24px -10px rgba(0, 0, 0, .25);
      border: 1px solid #eceae7;
      overflow: hidden;
    }

    .fpc-zoom-stack hr {
      height: 1px;
      border: 0;
      margin: 0;
      background: #eceae7;
    }

    .fpc-zoom-btn {
      width: 46px;
      height: 46px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #3d3d40;
    }

    .fpc-zoom-card {
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 8px 24px -10px rgba(0, 0, 0, .25);
      border: 1px solid #eceae7;
    }

    .fpc-zoom-accent {
      background: #c50e6c;
      color: #fff;
      border-radius: 14px;
      box-shadow: 0 10px 26px -8px rgba(197, 14, 108, .6);
    }

    .fpc-scalebar {
      position: absolute;
      left: 14px;
      bottom: 60px;
      display: flex;
      flex-direction: column;
      gap: 5px;
      pointer-events: none;
      z-index: 10;
    }

    .fpc-scalebar-rule {
      display: flex;
      align-items: flex-end;
      gap: 0;
      height: 12px;
    }

    .fpc-scalebar-tick {
      width: 2px;
      height: 12px;
      background: #5c5f66;
    }

    .fpc-scalebar-line {
      height: 2px;
      background: #5c5f66;
      align-self: flex-end;
    }

    .fpc-scalebar-label {
      font-size: 11.5px;
      font-weight: 700;
      color: #5c5f66;
      letter-spacing: .02em;
    }

    .fpc-hint {
      position: absolute;
      left: 14px;
      bottom: 18px;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 7px 13px;
      border-radius: 999px;
      background: rgba(255, 255, 255, .85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      box-shadow: 0 4px 14px -8px rgba(0, 0, 0, .22);
      font-size: 12px;
      font-weight: 500;
      /* #6b6b70, not the handover's #8a8a8e: composited over this pill's own rgba(255,255,255,.85) on the
         canvas's #e9e7e3 base that grey is 3.33:1 and fails WCAG AA; this one clears it at 5.13:1. Matches
         .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
      pointer-events: none;
      z-index: 10;
    }
  `],
  template: `
    <!-- The gesture surface. role="img" on purpose: its contents are pixels, not controls (see the class docs). -->
    <div #viewport class="fpc-surface" role="img" [attr.aria-label]="ariaLabel()"
         [style.cursor]="cursor()"
         (pointerdown)="onSurfaceDown($event)"
         (dblclick)="onDoubleClick()">

      <!-- 1 · Background image, world-transformed, beneath the grid. -->
      @if (background(); as bg) {
        <div class="fpc-layer" [style.z-index]="0"
             [style.transform]="worldTransform()" [style.transition]="'transform ' + viewTransition()">
          <div class="absolute bg-no-repeat" aria-hidden="true"
               [style.left.px]="bg.x" [style.top.px]="bg.y"
               [style.width.px]="bg.width" [style.height.px]="bg.height"
               [style.opacity]="bg.opacity"
               [style.background-image]="'url(' + bg.objectUrl + ')'"
               [style.background-size]="'100% 100%'"></div>
        </div>
      }

      <!-- 2 · Grid, screen space. Transparent base so the background image shows through. -->
      @if (!displayMode()) {
        <div class="pointer-events-none absolute inset-0" [style.z-index]="1"
             [style.background-image]="gridImage()"
             [style.background-size]="gridSizes()" [style.background-position]="gridPosition()"
             [style.transition]="'background-position ' + viewTransition()"></div>
      }

      <!-- 3 · World layer. Non-interactive by default; the shapes opt back in. -->
      <div class="fpc-layer" [style.z-index]="2"
           [style.transform]="worldTransform()" [style.transition]="'transform ' + viewTransition()">

        <!-- One SVG in world space: with no viewBox and overflow visible, its user units ARE centimetres, so
             every polygon can be drawn in raw stored coordinates. Strokes use vector-effect so their width and
             dash pattern stay constant on screen at any zoom. -->
        <svg class="absolute left-0 top-0 h-px w-px overflow-visible" aria-hidden="true">
          @for (area of areas(); track area.id) {
            @if (displayMode()) {
              <!-- Display mode: a quiet dashed room outline in the area's own colour. Barely-there fill (the
                   editor's 40 % would drown the markers) and NOT interactive — in a live service view a zone is a
                   caption for the room, not a thing you select. -->
              <polygon [attr.points]="pointsOf(area.nodes)"
                       [attr.fill]="withAlpha(area.color, 0.1)"
                       [attr.stroke]="withAlpha(area.color, 0.55)"
                       stroke-width="1.5" stroke-dasharray="8 6"
                       vector-effect="non-scaling-stroke" class="pointer-events-none"/>
            } @else {
              <polygon [attr.points]="pointsOf(area.nodes)"
                       [attr.fill]="withAlpha(area.color, area.id === selectedAreaId() ? 0.5 : 0.4)"
                       [attr.stroke]="area.color"
                       [attr.stroke-width]="area.id === selectedAreaId() ? 2.5 : 2"
                       [attr.stroke-dasharray]="area.id === selectedAreaId() ? null : '8 6'"
                       vector-effect="non-scaling-stroke"
                       class="pointer-events-auto cursor-move"
                       (pointerdown)="onAreaDown($event, area)"/>
            }
          }
          <!-- The polygon under construction, rubber-banding to the cursor. -->
          @if (draftPoints(); as draft) {
            <polyline [attr.points]="draft" [attr.fill]="withAlpha(accent, 0.1)" [attr.stroke]="accent"
                      stroke-width="2" stroke-dasharray="6 5" vector-effect="non-scaling-stroke"/>
          }
        </svg>

        <!-- Area names + the area-weighted centroid cross, in the area's own colour. The label is sized in
             world units that shrink with the zoom, so it stays a constant 13 px on screen.
             The name sits at the polygon's pole of inaccessibility (labelOf) and is CENTRED on it — unlike the
             centroid cross beside it, that point is guaranteed to be inside even a concave room. -->
        @for (area of areas(); track area.id) {
          <div class="pointer-events-none absolute whitespace-nowrap uppercase" aria-hidden="true"
               [style.left.px]="labelOf(area).x" [style.top.px]="labelOf(area).y"
               [style.transform]="'translate(-50%, -50%)'"
               [style.font-size.px]="areaLabelSize()" [style.font-weight]="700"
               [style.letter-spacing]="displayMode() ? '.08em' : '.02em'"
               [style.color]="displayMode() ? darken(area.color, AREA_LABEL_SHADE) : area.color"
               [style.filter]="displayMode() ? null : 'brightness(0.7)'">{{ area.name }}</div>

          <!-- The centroid cross is a layout aid ("this is the middle you are editing"); a read-only view has
               nothing to align, so it would only be one more mark competing with the markers. -->
          @if (!displayMode()) {
            <svg class="pointer-events-none absolute overflow-visible" aria-hidden="true"
                 viewBox="-12 -12 24 24" width="24" height="24"
                 [style.left.px]="centerOf(area).x" [style.top.px]="centerOf(area).y"
                 [style.transform]="'translate(-12px, -12px)'">
              <path d="M-8 -8L8 8M8 -8L-8 8" [attr.stroke]="withAlpha(area.color, 0.55)" stroke-width="2"
                    stroke-linecap="round" vector-effect="non-scaling-stroke"/>
            </svg>
          }
        }

        <!-- Tables. Plain divs so the shadows/radii of the handover render literally. -->
        @for (table of tables(); track table.id) {
          @if (decorationOf(table); as marker) {
            <!-- Display-mode marker: number + open total + a clock badge when the table needs attention. Every
                 size here is in WORLD units, so the marker scales with the plan exactly as the reference does. -->
            <div class="pointer-events-auto absolute flex touch-none cursor-pointer flex-col items-center
                        justify-center"
                 [attr.data-table-id]="table.id"
                 [style.left.px]="table.x" [style.top.px]="table.y"
                 [style.width.px]="table.width" [style.height.px]="table.height"
                 [style.gap.px]="2"
                 [style.border-radius]="table.tableShape === TableShape.Circle ? '999px' : '18px'"
                 [style.background]="table.id === selectedTableId() ? '#fdf1f7' : marker.surface"
                 [style.border]="'2px solid ' + (table.id === selectedTableId() ? accent : marker.border)"
                 [style.box-shadow]="markerShadow(table, marker.muted)"
                 [style.z-index]="table.id === selectedTableId() ? 5 : 2"
                 (pointerdown)="onTableDown($event, table)">
              @if (marker.badgeColor; as badge) {
                <span class="pointer-events-none absolute flex items-center justify-center rounded-full"
                      aria-hidden="true"
                      [style.top.px]="-markerBadgeOffsetCm" [style.right.px]="-markerBadgeOffsetCm"
                      [style.width.px]="markerBadgeCm" [style.height.px]="markerBadgeCm"
                      [style.background]="badge"
                      [style.box-shadow]="'0 2px 5px rgba(0, 0, 0, 0.25)'">
                  <svg [attr.width]="markerBadgeCm / 2" [attr.height]="markerBadgeCm / 2" viewBox="0 0 24 24"
                       fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9"/>
                    <path d="M12 7v5l3 2"/>
                  </svg>
                </span>
              }
              <span class="pointer-events-none overflow-hidden text-ellipsis whitespace-nowrap font-bold
                           leading-none"
                    [style.font-size.px]="markerNumberCm" [style.max-width.px]="table.width - 10"
                    [style.color]="table.id === selectedTableId() ? accent : marker.textColor">{{ marker.number }}</span>
              @if (marker.detail; as detail) {
                <!-- The handover renders this line at 85 % opacity; at full strength it is the same colour the
                     number already uses, which is what keeps it legible at a glance from across a room (and is
                     what a contrast check can actually measure). -->
                <span class="pointer-events-none whitespace-nowrap font-semibold leading-none tabular-nums"
                      [style.font-size.px]="markerDetailCm"
                      [style.color]="table.id === selectedTableId() ? accent : marker.textColor">{{ detail }}</span>
              }
            </div>
          } @else {
            <div class="pointer-events-auto absolute flex touch-none cursor-grab items-center justify-center"
                 [style.left.px]="table.x" [style.top.px]="table.y"
                 [style.width.px]="table.width" [style.height.px]="table.height"
                 [style.border-radius]="table.tableShape === TableShape.Circle ? '999px' : '16px'"
                 [style.background]="tableFill(table)"
                 [style.border]="'2px solid ' + tableStroke(table)"
                 [style.box-shadow]="tableShadow(table)"
                 [style.z-index]="table.id === selectedTableId() ? 5 : 2"
                 (pointerdown)="onTableDown($event, table)">
              <!-- Fixed on-canvas size: the number must not grow when the table is resized. -->
              <span class="pointer-events-none overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    [style.font-size.px]="tableLabelCm" [style.max-width.px]="table.width - 10"
                    [style.color]="table.id === selectedTableId() ? accent : '#1c1c1e'">{{ table.name }}</span>
            </div>
          }
        }

        <!-- The origin landmark — an editing aid (it is where coordinates are measured from), so it is not part of
             a read-only service view. -->
        @if (!displayMode()) {
          <div class="pointer-events-none absolute left-0 top-0" aria-hidden="true"
               [style.transform]="'translate(-50%, -50%)'">
            <div class="h-[22px] w-[22px] rounded-full"
                 [style.border]="'2px solid ' + accent"
                 [style.background]="withAlpha(accent, 0.08)"
                 [style.box-shadow]="'0 0 0 4px ' + withAlpha(accent, 0.12)"></div>
          </div>
          <div class="pointer-events-none absolute left-0 top-0 whitespace-nowrap text-[11px] font-semibold
                      opacity-75"
               aria-hidden="true" [style.transform]="'translate(10px, 9px)'" [style.color]="accent">0, 0</div>
        }
      </div>

      <!-- 4 · Handle layer: screen space, so handles keep one size at every zoom. Every handle in it MUTATES
           geometry, so display mode drops the layer entirely rather than hiding its contents one by one — that is
           what makes "no editor mutation can escape" a structural guarantee instead of a review promise. -->
      @if (!displayMode()) {
      <div class="pointer-events-none absolute inset-0 z-[4]" aria-hidden="true">
        @if (tableFrame(); as frame) {
          <div class="pointer-events-none absolute" [style.left.px]="frame.left" [style.top.px]="frame.top"
               [style.width.px]="frame.width" [style.height.px]="frame.height"
               [style.border]="'1.5px solid ' + accent" [style.border-radius]="frame.radius"></div>
          <div class="pointer-events-auto absolute cursor-nwse-resize bg-white"
               [style.left.px]="frame.left + frame.width - 7"
               [style.top.px]="frame.top + frame.height - 7"
               [style.width.px]="handlePx" [style.height.px]="handlePx"
               [style.border-radius.px]="4"
               [style.border]="'2px solid ' + accent"
               [style.box-shadow]="'0 2px 6px rgba(0, 0, 0, 0.2)'"
               (pointerdown)="onTableResizeDown($event)"></div>
        }

        @if (backgroundFrame(); as frame) {
          <div class="pointer-events-auto absolute cursor-move"
               [style.left.px]="frame.left" [style.top.px]="frame.top"
               [style.width.px]="frame.width" [style.height.px]="frame.height"
               [style.border-radius.px]="4"
               [style.border]="'2px solid ' + accent" [style.background]="withAlpha(accent, 0.04)"
               (pointerdown)="onBackgroundDown($event)"></div>
          <div class="pointer-events-auto absolute cursor-nwse-resize bg-white"
               [style.left.px]="frame.left + frame.width - backgroundHandlePx / 2"
               [style.top.px]="frame.top + frame.height - backgroundHandlePx / 2"
               [style.width.px]="backgroundHandlePx" [style.height.px]="backgroundHandlePx"
               [style.border-radius.px]="4"
               [style.border]="'2px solid ' + accent"
               [style.box-shadow]="'0 2px 6px rgba(0, 0, 0, 0.2)'"
               (pointerdown)="onBackgroundResizeDown($event)"></div>
        }

        <!-- Edge hit-zones (add a node). The WHOLE edge is the target, showing a copy cursor. -->
        @for (edge of edgeZones(); track edge.index) {
          <div class="pointer-events-auto absolute cursor-copy"
               [style.left.px]="edge.left" [style.top.px]="edge.top"
               [style.width.px]="edge.length" [style.height.px]="edgeZonePx"
               [style.transform-origin]="'0 ' + edgeZonePx / 2 + 'px'"
               [style.transform]="'rotate(' + edge.angle + 'deg)'"
               (pointerdown)="onEdgeDown($event, edge.index)"></div>
        }

        <!-- Vertex handles (drag to reshape, double-tap to remove). -->
        @for (vertex of vertexHandles(); track vertex.index) {
          <div class="pointer-events-auto absolute cursor-grab rounded-full bg-white"
               [style.left.px]="vertex.left" [style.top.px]="vertex.top"
               [style.width.px]="handlePx" [style.height.px]="handlePx"
               [style.border]="'2px solid ' + accent"
               [style.box-shadow]="'0 2px 6px rgba(0, 0, 0, 0.25)'"
               (pointerdown)="onVertexDown($event, vertex.index)"></div>
        }

        @for (dot of draftDots(); track $index) {
          <div class="pointer-events-none absolute rounded-full"
               [style.left.px]="dot.left" [style.top.px]="dot.top"
               [style.width.px]="draftDotPx" [style.height.px]="draftDotPx"
               [style.background]="dot.first ? accent : '#ffffff'"
               [style.border]="'2px solid ' + accent"></div>
        }
      </div>
      }
    </div>

    <!-- 5 · UI overlays. Outside the role="img" element, so the buttons are reachable and a press on one is not
         also a press on the canvas (which would start a pan and clear the selection). -->
    @if (!displayMode()) {
      <div class="fpc-readout" role="status" aria-live="off">
        <span>x&nbsp;<b>{{ readoutX() }}</b>&thinsp;m</span>
        <span>y&nbsp;<b>{{ readoutY() }}</b>&thinsp;m</span>
        <span class="fpc-divider"></span>
        <span [style.color]="accent" [style.font-weight]="700">{{ zoomPercent() }} %</span>
      </div>
    }

    @if (banner()) {
      <div class="fpc-banner" role="status">
        <span class="fpc-banner-dot"></span>{{ banner() }}
      </div>
    }

    <!-- Zoom cluster. Top-right in BOTH modes: originally to clear the app's fixed bottom-right account
         switcher, and kept there after its removal (see .fpc-zoom above for the full reasoning). -->
    <div class="fpc-zoom">
      <div class="fpc-zoom-stack">
        <button type="button" class="cq-btn fpc-zoom-btn" (click)="zoomIn()" aria-label="Vergrößern">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
               stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
        </button>
        <hr>
        <button type="button" class="cq-btn fpc-zoom-btn" (click)="zoomOut()" aria-label="Verkleinern">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
               stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>
        </button>
      </div>
      <button type="button" class="cq-btn fpc-zoom-btn fpc-zoom-card" (click)="fitAll()"
              aria-label="Alles zeigen" title="Alles zeigen">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15"/>
        </svg>
      </button>
      <!-- "Centre the origin" only means something while you are placing things against it. -->
      @if (!displayMode()) {
        <button type="button" class="cq-btn fpc-zoom-btn fpc-zoom-accent" (click)="centerOrigin()"
                aria-label="Ursprung zentrieren" title="Ursprung zentrieren">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3.2"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
          </svg>
        </button>
      }

      <!-- Host-supplied map-layer controls, projected into the camera cluster so a screen that adds one (Offene
           Tische's "freie Tische anzeigen") does not grow a second floating cluster beside this one. The host
           styles what it projects — emulated encapsulation scopes projected content to its DECLARING component,
           not to this one. -->
      <ng-content select="[canvasControl]"/>
    </div>

    @if (!displayMode()) {
      <!-- Scale bar. -->
      <div class="fpc-scalebar" aria-hidden="true">
        <div class="fpc-scalebar-rule">
          <span class="fpc-scalebar-tick"></span>
          <span class="fpc-scalebar-line" [style.width.px]="scaleBar().pixels"></span>
          <span class="fpc-scalebar-tick"></span>
        </div>
        <span class="fpc-scalebar-label">{{ scaleBar().label }}</span>
      </div>

      <div class="fpc-hint">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b6b4b0" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M3 16v3a2 2 0 0 0 2 2h3"/>
        </svg>
        {{ hint() }}
      </div>
    }
  `
})
export class FloorPlanCanvasComponent implements AfterViewInit {
  private readonly viewportRef = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly destroyRef = inject(DestroyRef);

  readonly tables = input<readonly FloorTable[]>([]);
  readonly areas = input<readonly FloorArea[]>([]);
  /** The fetched background image, or `null` when the shard has none. */
  readonly background = input<RenderedBackground | null>(null);
  readonly mode = input<FloorPlanMode>('select');
  readonly selectedTableId = input<string | null>(null);
  readonly selectedAreaId = input<string | null>(null);
  /** Whether the background image is the current selection (which is what shows its move/resize frame). */
  readonly backgroundSelected = input(false);
  /** Vertices of the polygon currently being drawn (draw mode). */
  readonly draftNodes = input<readonly AreaNode[]>([]);

  /**
   * **Read-only display mode** (#205): the canvas becomes a live *service* view of the same floor plan.
   *
   * Additive on purpose — the editor's behaviour is untouched. What changes: the grid, origin landmark, coordinate
   * readout, scale bar, hint, centroid crosses and the whole handle layer are gone; areas become non-interactive
   * room outlines; tables render as {@link decorations} markers; the zoom cluster keeps its top-right anchor but
   * loses the centre-origin button; the camera clamps to {@link DISPLAY_SCALE_RANGE} and
   * frames the whole plan on first paint instead of centring the origin; and empty canvas deselects on a *click*
   * rather than on pointer-down, so panning no longer throws the selection away.
   *
   * A host may add its own map-layer buttons to the camera cluster by projecting them with the `canvasControl`
   * attribute (see the `<ng-content>` slot in the template).
   *
   * No mutation output can fire in this mode: `tableMoved`/`tableResized`/`areaMoved`/`areaNode*`/`background*`
   * all originate in gestures that are either suppressed here or in a layer that is not rendered at all.
   */
  readonly displayMode = input(false);

  /**
   * Per-table marker decoration, keyed by table id — only consulted in {@link displayMode}. A table with no entry
   * falls back to the plain editor marker, which is what keeps a display-mode canvas rendering sensibly while its
   * host is still resolving levels.
   */
  readonly decorations = input<Readonly<Record<string, TableDecoration>>>({});

  /** Minor grid spacing in centimetres (10 / 20 / 50 / 100). Thick lines are always at 1 m regardless. */
  readonly minorGridCm = input(50);
  /** Whether the grid is drawn at all. */
  readonly gridVisible = input(true);
  /** Whether moves/resizes/vertices/the origin round onto the minor grid. */
  readonly snapEnabled = input(true);

  readonly tableSelected = output<string>();
  readonly areaSelected = output<string>();
  /** A press on empty canvas in select mode: the host clears its selection. */
  readonly selectionCleared = output<void>();
  readonly tableMoved = output<{id: string; x: number; y: number}>();
  readonly tableResized = output<{id: string; width: number; height: number}>();
  /** An area dragged as a whole: the complete, already-snapped node list. */
  readonly areaMoved = output<{areaId: string; nodes: AreaNode[]}>();
  readonly areaNodeMoved = output<{areaId: string; index: number; x: number; y: number}>();
  /** A click on an edge: insert a vertex **after** `index` at the clicked point. */
  readonly areaNodeAdded = output<{areaId: string; index: number; x: number; y: number}>();
  /** A double-tapped vertex: remove it (the host enforces the minimum too). */
  readonly areaNodeRemoved = output<{areaId: string; index: number}>();
  /** In draw mode: the (snapped) point the admin clicked, i.e. the next polygon vertex. */
  readonly canvasPointed = output<AreaNode>();
  /** The draft should be closed into a real area (double-click, first vertex re-clicked, or Enter). */
  readonly draftClosed = output<void>();
  /** The draft should be thrown away (Esc). */
  readonly draftCancelled = output<void>();
  /** In origin mode: the (snapped) world point that should become `(0, 0)`. */
  readonly originPicked = output<AreaNode>();
  readonly backgroundMoved = output<{x: number; y: number}>();
  /** Aspect-locked: only the width is emitted; the height always follows from the image's own ratio. */
  readonly backgroundResized = output<{width: number}>();
  /** Delete/Backspace on the current selection (never while typing in a form control). */
  readonly deleteRequested = output<void>();

  protected readonly TableShape = TableShape;
  protected readonly accent = EDITOR_ACCENT;
  protected readonly tableLabelCm = TABLE_LABEL_CM;
  protected readonly markerNumberCm = MARKER_NUMBER_CM;
  protected readonly markerDetailCm = MARKER_DETAIL_CM;
  protected readonly markerBadgeCm = MARKER_BADGE_CM;
  protected readonly markerBadgeOffsetCm = MARKER_BADGE_OFFSET_CM;
  protected readonly handlePx = HANDLE_PX;
  protected readonly backgroundHandlePx = BACKGROUND_HANDLE_PX;
  protected readonly edgeZonePx = EDGE_ZONE_PX;
  protected readonly draftDotPx = DRAFT_DOT_PX;
  protected readonly withAlpha = withAlpha;
  protected readonly darken = darken;
  protected readonly AREA_LABEL_SHADE = AREA_LABEL_SHADE;

  /** The camera. The canvas owns this and nothing else — all *data* lives in the host. */
  private readonly view = signal<ViewTransform>({x: 0, y: 0, scale: 1});
  /** Viewport size in pixels, tracked so fit-all/center-origin and the readout have something real to work with. */
  private readonly viewportSize = signal({width: 0, height: 0});
  /** Cursor position in world units, or `null` while the pointer is outside the canvas. */
  private readonly cursorWorld = signal<AreaNode | null>(null);
  private readonly panning = signal(false);
  /**
   * Whether a button-driven camera move is in flight, which is what turns the CSS transition on.
   *
   * The transition must be **off** for pan and wheel-zoom: easing a gesture that already tracks the pointer makes
   * the world lag behind the cursor, which is exactly the "swimming" feel the handover avoids.
   */
  private readonly animating = signal(false);
  private animationTimer: ReturnType<typeof setTimeout> | null = null;

  private gesture: Gesture | null = null;
  /** Last vertex pointer-down, for the hand-timed double-tap that removes a node (see `isDoubleTap`). */
  private lastVertexTap: {index: number; at: number} | null = null;
  /**
   * Whether display mode has already framed the plan once.
   *
   * Needed because the geometry arrives *after* the view initialises — `ngAfterViewInit` runs against an empty
   * `tables()`/`areas()`, where `fitAllView` degenerates to "centre the origin". The effect below therefore waits
   * for the first non-empty content bounds and fits then, exactly once, so a later live push does not yank the
   * camera back from wherever the user panned it.
   */
  private displayFitDone = false;

  /** Set once the viewport has been measured; before that a fit would frame against a 1 × 1 box. */
  private viewInitialized = false;

  constructor() {
    // Display mode's "frame everything on mount", for the normal case where the geometry arrives AFTER the view
    // initialises (an HTTP round trip). Reading the geometry signals is what subscribes this effect to them, so it
    // re-runs when the first tables/areas land — see `displayFitDone` for why it fires only once.
    effect(() => {
      const tables = this.tables();
      const areas = this.areas();
      if (!this.displayMode() || this.displayFitDone || !this.viewInitialized) return;
      if (contentBounds(tables, areas, this.background()) === null) return;
      this.displayFitDone = true;
      this.fitAll();
    });
  }

  ngAfterViewInit(): void {
    const element = this.viewportRef().nativeElement;

    this.measure();
    this.viewInitialized = true;
    // Start with the origin centred, per the handoff's §1. Display mode instead frames the whole plan — done here
    // (not only in the effect) because geometry that is ALREADY present when the view initialises would otherwise
    // be framed first and then immediately overwritten by this centring.
    if (this.displayMode() && contentBounds(this.tables(), this.areas(), this.background()) !== null) {
      this.displayFitDone = true;
      this.fitAll();
    } else {
      this.view.set(centerOriginView(this.viewportSize().width, this.viewportSize().height));
    }

    // Non-passive so the wheel gesture can preventDefault and zoom instead of scrolling the page.
    const onWheel = (event: WheelEvent) => this.onWheel(event);
    element.addEventListener('wheel', onWheel, {passive: false});

    // Dragging must survive the pointer leaving the canvas, so move/up are window-level.
    const onMove = (event: PointerEvent) => this.onWindowMove(event);
    const onUp = () => this.endGesture();
    const onKey = (event: KeyboardEvent) => this.onKeyDown(event);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);

    const observer = new ResizeObserver(() => this.measure());
    observer.observe(element);

    this.destroyRef.onDestroy(() => {
      element.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
      observer.disconnect();
      if (this.animationTimer) clearTimeout(this.animationTimer);
    });
  }

  // region Derived render state

  /** The whole canvas is one labelled image; the label states what it contains (see the class docs). */
  protected readonly ariaLabel = computed(() => this.displayMode()
    ? `Raumplan mit ${this.tables().length} Tischen in ${this.areas().length} Bereichen`
    : `Grundriss mit ${this.tables().length} Tischen und ${this.areas().length} Bereichen`);

  protected readonly worldTransform = computed(() => {
    const view = this.view();
    return `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
  });

  /** The easing applied to the world layers — only while a button-driven camera move is running. */
  protected readonly viewTransition = computed(() => this.animating() ? VIEW_ANIMATION : '0s');

  /**
   * The grid as four CSS gradients: two thin lines at the minor spacing and two thicker ones at **1 m**, which is
   * fixed regardless of the minor spacing (the handoff's §3) so the metre rhythm of the room never changes.
   */
  protected readonly gridImage = computed(() => this.gridVisible()
    ? 'linear-gradient(#d7d3cc 1px, transparent 1px), linear-gradient(90deg, #d7d3cc 1px, transparent 1px), ' +
      'linear-gradient(#c9c4bc 1.5px, transparent 1.5px), linear-gradient(90deg, #c9c4bc 1.5px, transparent 1.5px)'
    : 'none');

  protected readonly gridSizes = computed(() => {
    const scale = this.view().scale;
    const minor = this.minorGridCm() * scale;
    const major = 100 * scale;
    return `${minor}px ${minor}px, ${minor}px ${minor}px, ${major}px ${major}px, ${major}px ${major}px`;
  });

  /** The grid is anchored to the origin, so it pans with the world even though it is drawn in screen space. */
  protected readonly gridPosition = computed(() => `${this.view().x}px ${this.view().y}px`);

  /** An area's name label in world units, so it renders at a constant {@link AREA_LABEL_PX} on screen. */
  protected readonly areaLabelSize = computed(() => AREA_LABEL_PX / this.view().scale);

  protected readonly cursor = computed(() => {
    if (this.panning()) return 'grabbing';
    return this.mode() === 'select' ? 'grab' : 'crosshair';
  });

  protected readonly zoomPercent = computed(() => Math.round(this.view().scale * 100));
  protected readonly readoutX = computed(() => formatMetres(this.cursorWorld()?.x ?? 0));
  protected readonly readoutY = computed(() => formatMetres(this.cursorWorld()?.y ?? 0));
  protected readonly scaleBar = computed(() => scaleBarFor(this.view().scale));

  protected readonly banner = computed(() => {
    switch (this.mode()) {
      case 'origin':
        return 'Ursprung setzen: Punkt anklicken – alle Positionen werden neu berechnet';
      case 'draw':
        return 'Bereich zeichnen: klicken für Eckpunkte · Doppelklick oder ersten Punkt schließt · Esc bricht ab';
      default:
        return null;
    }
  });

  protected readonly hint = computed(() => {
    switch (this.mode()) {
      case 'origin':
        return 'Klick platziert den neuen Ursprung (0, 0)';
      case 'draw':
        return 'Klicken setzt Eckpunkte';
      default:
        return 'Ziehen zum Verschieben · Scrollen zum Zoomen';
    }
  });

  /** The selected table's screen-space bounding frame, or `null`. */
  protected readonly tableFrame = computed(() => {
    const table = this.tables().find(t => t.id === this.selectedTableId());
    if (!table) return null;
    const view = this.view();
    const topLeft = toScreen(view, table.x, table.y);
    const bottomRight = toScreen(view, table.x + table.width, table.y + table.height);
    return {
      left: topLeft.x,
      top: topLeft.y,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
      // A round table's frame is a full radius; a square one's matches its own 16 cm corner at the current zoom.
      radius: table.tableShape === TableShape.Circle ? '999px' : `${16 * view.scale}px`
    };
  });

  /** The background image's screen-space frame while it is selected, or `null`. */
  protected readonly backgroundFrame = computed(() => {
    const bg = this.background();
    if (!bg || !this.backgroundSelected()) return null;
    const view = this.view();
    const topLeft = toScreen(view, bg.x, bg.y);
    const bottomRight = toScreen(view, bg.x + bg.width, bg.y + bg.height);
    return {
      left: topLeft.x,
      top: topLeft.y,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y
    };
  });

  /** Screen positions of the selected area's vertex handles. */
  protected readonly vertexHandles = computed(() => {
    const area = this.selectedArea();
    if (!area) return [];
    const view = this.view();
    return area.nodes.map((node, index) => {
      const point = toScreen(view, node.x, node.y);
      return {index, left: point.x - 7, top: point.y - 7};
    });
  });

  /**
   * The selected area's edge hit-zones: one rotated strip per edge, so the **whole** edge inserts a node rather
   * than only a midpoint dot (the handoff's §5).
   */
  protected readonly edgeZones = computed(() => {
    const area = this.selectedArea();
    if (!area) return [];
    const view = this.view();
    return area.nodes.map((node, index) => {
      const next = area.nodes[(index + 1) % area.nodes.length];
      const from = toScreen(view, node.x, node.y);
      const to = toScreen(view, next.x, next.y);
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      return {
        index,
        left: from.x,
        top: from.y - EDGE_ZONE_PX / 2,
        length: Math.hypot(dx, dy),
        angle: (Math.atan2(dy, dx) * 180) / Math.PI
      };
    });
  });

  /** The draft polyline, rubber-banding from the last placed vertex to the cursor. */
  protected readonly draftPoints = computed(() => {
    const nodes = this.draftNodes();
    if (this.mode() !== 'draw' || nodes.length === 0) return null;
    const cursor = this.cursorWorld();
    const all = cursor ? [...nodes, cursor] : nodes;
    return all.map(node => `${node.x},${node.y}`).join(' ');
  });

  /** Screen dots for the placed draft vertices; the first is filled once the polygon can be closed on it. */
  protected readonly draftDots = computed(() => {
    const nodes = this.draftNodes();
    if (this.mode() !== 'draw' || nodes.length === 0) return [];
    const view = this.view();
    return nodes.map((node, index) => {
      const point = toScreen(view, node.x, node.y);
      return {left: point.x - 6, top: point.y - 6, first: index === 0 && nodes.length >= MIN_AREA_NODES};
    });
  });

  /** `points` attribute for a polygon/polyline, in raw world units. */
  pointsOf(nodes: readonly AreaNode[]): string {
    return nodes.map(node => `${node.x},${node.y}`).join(' ');
  }

  /**
   * Where an area's name label goes: the polygon's **pole of inaccessibility**, the point furthest from any edge.
   *
   * Replaces the bounding-box top-left corner this used to anchor captions at. That corner is fine on a rectangle
   * but is frequently a spot an L/U/T-shaped room does not occupy, so the caption rendered on empty canvas beside
   * its own shape — the reported "Bar" bug. This point is inside the polygon by construction.
   *
   * Computed from the **current** nodes rather than read off `area.labelX`/`labelY` for the same reason
   * {@link centerOf} is: it keeps the caption under the shape while an area is being dragged or reshaped, and
   * because it is the identical formula the two agree exactly for a saved area.
   */
  protected labelOf(area: FloorArea): AreaNode {
    return polygonLabelAnchor(area.nodes);
  }

  /**
   * Where the centroid cross goes.
   *
   * Computed from the **current** nodes with the same shoelace formula the backend persists, rather than read off
   * `area.centerX`/`centerY`: that is what keeps the marker under the shape while an area is being dragged or
   * reshaped, and because it is the identical formula the two agree exactly for a saved area.
   */
  protected centerOf(area: FloorArea): AreaNode {
    return polygonCentroid(area.nodes);
  }

  /**
   * A table has exactly two appearances, selected and not — the handover's own.
   *
   * Live occupancy is deliberately **not** drawn here even though `status` is on the wire: this screen lays out
   * the room, and tinting half the plan by who happens to be sitting where would make the layout harder to read
   * (and is not a state the reference implementation has).
   */
  protected tableFill(table: FloorTable): string {
    return table.id === this.selectedTableId() ? '#fdf1f7' : '#ffffff';
  }

  protected tableStroke(table: FloorTable): string {
    return table.id === this.selectedTableId() ? EDITOR_ACCENT : '#dcd8d2';
  }

  protected tableShadow(table: FloorTable): string {
    return table.id === this.selectedTableId()
      ? `0 0 0 4px ${withAlpha(EDITOR_ACCENT, 0.14)}, 0 10px 24px -10px rgba(0, 0, 0, 0.25)`
      : '0 4px 12px -6px rgba(0, 0, 0, 0.18)';
  }

  /**
   * The marker decoration for a table, or `null` — which is also what the template branches on, so an editor
   * canvas (no `displayMode`, no `decorations`) can never accidentally take the marker path.
   */
  protected decorationOf(table: FloorTable): TableDecoration | null {
    if (!this.displayMode()) return null;
    return this.decorations()[table.id] ?? null;
  }

  /**
   * The handover's marker shadow — a deeper, accent-tinted lift while selected.
   *
   * A muted marker gets **no** lift at all: that, rather than a blanket element opacity, is what pushes it behind
   * the tables that actually need attention while keeping its own text at full, measurable contrast.
   */
  protected markerShadow(table: FloorTable, muted = false): string {
    if (table.id === this.selectedTableId()) {
      return `0 0 0 4px ${withAlpha(EDITOR_ACCENT, 0.16)}, 0 10px 24px -10px rgba(0, 0, 0, 0.3)`;
    }
    return muted ? 'none' : '0 4px 12px -6px rgba(0, 0, 0, 0.18)';
  }

  private selectedArea(): FloorArea | null {
    return this.areas().find(a => a.id === this.selectedAreaId()) ?? null;
  }

  // endregion

  // region Camera

  /** The zoom range this canvas allows — narrower in display mode (see {@link DISPLAY_SCALE_RANGE}). */
  protected scaleRange(): ScaleRange {
    return this.displayMode() ? DISPLAY_SCALE_RANGE : EDITOR_SCALE_RANGE;
  }

  /** Zooms in around the middle of the viewport. */
  zoomIn(): void {
    const {width, height} = this.viewportSize();
    this.animateTo(zoomCentered(this.view(), ZOOM_IN_FACTOR, width, height, this.scaleRange()));
  }

  /** Zooms out around the middle of the viewport. */
  zoomOut(): void {
    const {width, height} = this.viewportSize();
    this.animateTo(zoomCentered(this.view(), ZOOM_OUT_FACTOR, width, height, this.scaleRange()));
  }

  /** Frames every placed object. Falls back to {@link centerOrigin} for an empty plan. */
  fitAll(): void {
    const {width, height} = this.viewportSize();
    this.animateTo(fitAllView(this.tables(), this.areas(), this.background(), width, height, this.scaleRange()));
  }

  /** Re-centres the origin in the view at 100 %. Moves the camera only — no geometry is touched. */
  centerOrigin(): void {
    const {width, height} = this.viewportSize();
    this.animateTo(centerOriginView(width, height));
  }

  /**
   * Applies a camera move with the handover's easing, and switches the transition back off once it has played out
   * so the next pan/wheel gesture tracks the pointer exactly.
   */
  private animateTo(view: ViewTransform): void {
    this.animating.set(true);
    this.view.set(view);
    if (this.animationTimer) clearTimeout(this.animationTimer);
    this.animationTimer = setTimeout(() => {
      this.animationTimer = null;
      this.animating.set(false);
    }, VIEW_ANIMATION_MS);
  }

  /** The current view transform, for tests and for the host's "nothing moved visually" origin compensation. */
  currentView(): ViewTransform {
    return this.view();
  }

  /**
   * The world point currently in the middle of the viewport — where the host places a newly added table, so it
   * appears where the admin is looking rather than at an origin that may be far off screen.
   */
  viewCenterWorld(): AreaNode {
    const {width, height} = this.viewportSize();
    const world = toWorld(this.view(), width / 2, height / 2);
    return {x: Math.round(world.x), y: Math.round(world.y)};
  }

  /**
   * Applies a view transform the host computed — used by the move-origin flow, which has to compensate the camera
   * by exactly the shift it applied to the geometry so nothing appears to move.
   */
  applyView(view: ViewTransform): void {
    this.stopAnimation();
    this.view.set(view);
  }

  /** Cancels an in-flight eased move, so a gesture starting mid-animation is not smeared by it. */
  private stopAnimation(): void {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
    this.animating.set(false);
  }

  private measure(): void {
    const box = this.viewportRef().nativeElement.getBoundingClientRect();
    // A detached element (or a Storybook frame mid-layout) reports 0; fall back so the maths never divides by zero.
    this.viewportSize.set({width: box.width || 1, height: box.height || 1});
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.stopAnimation();
    this.view.update(view =>
      zoomAround(view, wheelFactor(event.deltaY), this.screenPoint(event), this.scaleRange()));
  }

  // endregion

  // region Gestures

  /**
   * A press on the surface itself: pans in select mode, places the origin in origin mode, and drops (or closes) a
   * polygon vertex in draw mode.
   */
  onSurfaceDown(event: PointerEvent): void {
    if (this.gesture) return;
    const world = this.worldPoint(event);
    const grid = this.minorGridCm();
    const snap = this.snapEnabled();

    if (this.mode() === 'origin') {
      this.originPicked.emit({x: snapTo(world.x, grid, snap), y: snapTo(world.y, grid, snap)});
      return;
    }

    if (this.mode() === 'draw') {
      const point = {x: snapTo(world.x, grid, snap), y: snapTo(world.y, grid, snap)};
      const nodes = this.draftNodes();
      // Clicking the first vertex again closes the polygon — within a constant SCREEN distance, so the target does
      // not shrink as you zoom out.
      if (nodes.length >= MIN_AREA_NODES) {
        const first = nodes[0];
        if (Math.hypot(point.x - first.x, point.y - first.y) * this.view().scale < CLOSE_DRAFT_PX) {
          this.draftClosed.emit();
          return;
        }
      }
      this.canvasPointed.emit(point);
      return;
    }

    this.stopAnimation();
    const view = this.view();
    this.gesture = {
      kind: 'pan', startX: event.clientX, startY: event.clientY, originX: view.x, originY: view.y, moved: false
    };
    this.panning.set(true);
    // Display mode defers this to the RELEASE (see `endGesture`): here a press is the start of a possible pan, and
    // clearing the selection now would empty the list card the moment the user grabs the map to look around.
    if (!this.displayMode()) this.selectionCleared.emit();
  }

  onTableDown(event: PointerEvent, table: FloorTable): void {
    // While drawing or placing the origin a table must not swallow the click — the tool wins.
    if (this.mode() !== 'select') return;
    event.stopPropagation();
    this.tableSelected.emit(table.id);
    // A display-mode marker is a target, not a handle: selecting it must not start a move gesture.
    if (this.displayMode()) return;
    this.gesture = {
      kind: 'table', id: table.id,
      startX: event.clientX, startY: event.clientY, originX: table.x, originY: table.y
    };
  }

  onTableResizeDown(event: PointerEvent): void {
    const table = this.tables().find(t => t.id === this.selectedTableId());
    if (!table) return;
    event.stopPropagation();
    this.gesture = {
      kind: 'tableResize', id: table.id,
      startX: event.clientX, startY: event.clientY, originWidth: table.width, originHeight: table.height
    };
  }

  onAreaDown(event: PointerEvent, area: FloorArea): void {
    if (this.mode() !== 'select') return;
    event.stopPropagation();
    this.areaSelected.emit(area.id);
    this.gesture = {
      kind: 'area', id: area.id,
      startX: event.clientX, startY: event.clientY, originNodes: area.nodes.map(node => ({...node}))
    };
  }

  /**
   * A press on a vertex handle: removes it when it completes a double-tap, otherwise starts a reshape drag.
   *
   * The double-tap is **timed by hand** rather than read off a native `dblclick`, because these handles are
   * re-created on every re-render and the browser only fires `dblclick` when both presses hit the same element
   * instance (see `DOUBLE_TAP_MS`).
   */
  onVertexDown(event: PointerEvent, index: number): void {
    event.stopPropagation();
    const area = this.selectedArea();
    if (!area) return;

    const now = Date.now();
    if (isDoubleTap(this.lastVertexTap, index, now)) {
      this.lastVertexTap = null;
      if (area.nodes.length > MIN_AREA_NODES) this.areaNodeRemoved.emit({areaId: area.id, index});
      return;
    }
    this.lastVertexTap = {index, at: now};
    this.gesture = {
      kind: 'vertex', id: area.id, index,
      startX: event.clientX, startY: event.clientY, origin: {...area.nodes[index]}
    };
  }

  /** A press on an edge: inserts a vertex at the clicked point and immediately starts dragging it. */
  onEdgeDown(event: PointerEvent, index: number): void {
    event.stopPropagation();
    const area = this.selectedArea();
    if (!area) return;

    const world = this.worldPoint(event);
    const point = {
      x: snapTo(world.x, this.minorGridCm(), this.snapEnabled()),
      y: snapTo(world.y, this.minorGridCm(), this.snapEnabled())
    };
    this.areaNodeAdded.emit({areaId: area.id, index, ...point});
    // The new vertex lands at index + 1, and the gesture continues into it so it can be positioned in one motion.
    this.gesture = {
      kind: 'vertex', id: area.id, index: index + 1,
      startX: event.clientX, startY: event.clientY, origin: point
    };
  }

  onBackgroundDown(event: PointerEvent): void {
    const bg = this.background();
    if (!bg || this.mode() !== 'select') return;
    event.stopPropagation();
    this.gesture = {
      kind: 'background', startX: event.clientX, startY: event.clientY, originX: bg.x, originY: bg.y
    };
  }

  onBackgroundResizeDown(event: PointerEvent): void {
    const bg = this.background();
    if (!bg) return;
    event.stopPropagation();
    this.gesture = {kind: 'backgroundResize', startX: event.clientX, startY: event.clientY, originWidth: bg.width};
  }

  onDoubleClick(): void {
    if (this.mode() === 'draw' && this.draftNodes().length >= MIN_AREA_NODES) this.draftClosed.emit();
  }

  /**
   * Drives the in-flight gesture and keeps the coordinate readout live.
   *
   * Movement is emitted continuously (rather than only on release) so the host's draft — and therefore what is on
   * screen, and the table inspector's live *Bereich* row — follows the pointer.
   */
  private onWindowMove(event: PointerEvent): void {
    const view = this.view();
    const gesture = this.gesture;

    if (gesture) {
      // Screen delta converted into world units, so a drag tracks the cursor at any zoom.
      const dx = (event.clientX - gesture.startX) / view.scale;
      const dy = (event.clientY - gesture.startY) / view.scale;
      const grid = this.minorGridCm();
      const snap = this.snapEnabled();

      switch (gesture.kind) {
        case 'pan':
          // Manhattan distance against a small slop, matching the handover: a release that never really travelled
          // is a click (and therefore a deselect), not a pan.
          if (Math.abs(event.clientX - gesture.startX) + Math.abs(event.clientY - gesture.startY) > CLICK_SLOP_PX) {
            gesture.moved = true;
          }
          // Panning is a pure screen-space translation — no scale division, no snapping.
          this.view.set({
            ...view,
            x: gesture.originX + (event.clientX - gesture.startX),
            y: gesture.originY + (event.clientY - gesture.startY)
          });
          break;

        case 'table':
          this.tableMoved.emit({
            id: gesture.id,
            x: snapTo(gesture.originX + dx, grid, snap),
            y: snapTo(gesture.originY + dy, grid, snap)
          });
          break;

        case 'tableResize':
          // The top-left corner stays put, so only the size changes — never below the minimum footprint.
          this.tableResized.emit({
            id: gesture.id,
            width: Math.max(MIN_TABLE_SIZE, snapTo(gesture.originWidth + dx, grid, snap)),
            height: Math.max(MIN_TABLE_SIZE, snapTo(gesture.originHeight + dy, grid, snap))
          });
          break;

        case 'area':
          this.areaMoved.emit({
            areaId: gesture.id,
            nodes: gesture.originNodes.map(node => ({
              x: snapTo(node.x + dx, grid, snap),
              y: snapTo(node.y + dy, grid, snap)
            }))
          });
          break;

        case 'vertex':
          this.areaNodeMoved.emit({
            areaId: gesture.id,
            index: gesture.index,
            x: snapTo(gesture.origin.x + dx, grid, snap),
            y: snapTo(gesture.origin.y + dy, grid, snap)
          });
          break;

        case 'background':
          this.backgroundMoved.emit({
            x: snapTo(gesture.originX + dx, grid, snap),
            y: snapTo(gesture.originY + dy, grid, snap)
          });
          break;

        case 'backgroundResize':
          // Width only — the height is derived from the image's aspect ratio, so the resize is aspect-locked by
          // construction. Not snapped: a traced plan is aligned to its own features, not to the grid.
          this.backgroundResized.emit({width: Math.max(MIN_BACKGROUND_WIDTH, gesture.originWidth + dx)});
          break;
      }
    }

    const point = this.screenPoint(event);
    const {width, height} = this.viewportSize();
    if (point.x >= 0 && point.y >= 0 && point.x <= width && point.y <= height) {
      const world = toWorld(view, point.x, point.y);
      this.cursorWorld.set({x: Math.round(world.x), y: Math.round(world.y)});
    } else {
      this.cursorWorld.set(null);
    }
  }

  private endGesture(): void {
    const gesture = this.gesture;
    this.gesture = null;
    this.panning.set(false);
    // Display mode's "tap empty space to deselect" (see `onSurfaceDown`).
    if (this.displayMode() && gesture?.kind === 'pan' && !gesture.moved) this.selectionCleared.emit();
  }

  /**
   * The keyboard shortcuts of the handoff's §11 — Esc cancels/deselects, Enter closes a draft, Delete/Backspace
   * removes the selection.
   *
   * Bound at window level (a canvas gesture surface is not a focus target), but **inert while a form control has
   * focus**: the inspector is full of text inputs, where Backspace has to delete a character and Enter has to stay
   * a plain keypress rather than closing a polygon.
   */
  private onKeyDown(event: KeyboardEvent): void {
    if (this.isTyping()) return;

    if (event.key === 'Escape') {
      if (this.draftNodes().length > 0 || this.mode() === 'draw') this.draftCancelled.emit();
      else this.selectionCleared.emit();
      return;
    }
    if (event.key === 'Enter' && this.mode() === 'draw' && this.draftNodes().length >= MIN_AREA_NODES) {
      this.draftClosed.emit();
      return;
    }
    // Nothing is deletable from a read-only view; Escape (handled above) still clears the selection there.
    if (this.displayMode()) return;
    if (event.key === 'Delete' || event.key === 'Backspace') this.deleteRequested.emit();
  }

  /** Whether focus currently sits in something that consumes keystrokes. */
  private isTyping(): boolean {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
      (active as HTMLElement).isContentEditable === true;
  }

  /** Pointer position in viewport-relative screen pixels. */
  private screenPoint(event: {clientX: number; clientY: number}): {x: number; y: number} {
    const box = this.viewportRef().nativeElement.getBoundingClientRect();
    return {x: event.clientX - box.left, y: event.clientY - box.top};
  }

  /** Pointer position in world units (centimetres). */
  private worldPoint(event: {clientX: number; clientY: number}): AreaNode {
    const point = this.screenPoint(event);
    return toWorld(this.view(), point.x, point.y);
  }

  // endregion
}
